import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm, useWatch } from "react-hook-form";
import { Link, useSearchParams } from "react-router";
import Select from "react-select";
import { toast } from "react-toastify";
import { FaChevronLeft, FaChevronRight, FaDownload } from "react-icons/fa";
import { createClient, deactivateClient, deleteClient, getClient, listAllClients, listClientsPage, listFields, updateClient } from "../../../utils/cdesApi";
import { customStyles } from "../../../utils/styleUtils";
import ConfirmationDialog from "../../shared/ConfirmationDialog";
import FormCheckbox from "../../shared/FormCheckbox";
import { columnCellProps } from "./catalogColumns";
import { clientGridLayout } from "./clientViewColumns";
import { downloadCsv } from "./cdesCsv";
import ClientFormDialog from "./ClientFormDialog";
import "./CdesGrid.scss";

const PAGE_SIZE = 50;

const isBlank = (value) => value == null || value === "";

const displayValue = (value) => (isBlank(value) ? "—" : String(value));

const STATUS_COLUMN = { key: "status", label: "Status", frozen: true, width: 110 };
const ACTIONS_COLUMN = { key: "actions", label: "Actions", frozen: true, width: 280 };

const statusLabel = (status) => (status === "INACTIVE" ? "Inactive" : "Active");
const statusTone = (status) => (status === "INACTIVE" ? "muted" : "ok");

const formatPlanCount = (count) => `${count} ${count === 1 ? "plan" : "plans"}`;

const ClientView = () => {
    const queryClient = useQueryClient();
    const [searchParams, setSearchParams] = useSearchParams();
    const clientId = searchParams.get("clientId") || "";
    const [cursorStack, setCursorStack] = useState([undefined]);
    const cursor = cursorStack[cursorStack.length - 1];
    const [exporting, setExporting] = useState(false);
    const [exportError, setExportError] = useState("");
    const [formMode, setFormMode] = useState(null);
    const [editingClient, setEditingClient] = useState(null);
    const [formError, setFormError] = useState("");
    const [saving, setSaving] = useState(false);
    const [deactivateTarget, setDeactivateTarget] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [reactivatingId, setReactivatingId] = useState("");
    const inactiveForm = useForm({ defaultValues: { showInactiveClients: false } });
    const showInactiveClients = useWatch({ control: inactiveForm.control, name: "showInactiveClients" });

    const clientsQuery = useQuery({
        queryKey: ["cdes-clients", showInactiveClients],
        queryFn: () => listAllClients({ includeInactive: showInactiveClients }),
    });
    const fieldsQuery = useQuery({
        queryKey: ["cdes-field-columns", "client"],
        queryFn: () => listFields({ scope: "client" }),
    });
    const layout = useMemo(
        () => clientGridLayout(fieldsQuery.data?.fields, [STATUS_COLUMN, ACTIONS_COLUMN]),
        [fieldsQuery.data?.fields],
    );
    const cellProps = (column, extraClassName) => columnCellProps(column, layout, extraClassName);

    const pageQuery = useQuery({
        queryKey: ["cdes-client-rows", clientId, cursor, showInactiveClients],
        queryFn: async () => {
            if (clientId) {
                const client = await getClient(clientId);
                if (!showInactiveClients && client?.status === "INACTIVE") return { clients: [] };
                return { clients: [client] };
            }
            return listClientsPage({ cursor, limit: PAGE_SIZE, includeInactive: showInactiveClients });
        },
    });

    const clients = pageQuery.data?.clients ?? [];
    const nextCursor = pageQuery.data?.nextCursor;

    const selectedClientName = useMemo(() => {
        const match = (clientsQuery.data ?? []).find((client) => client.clientId === clientId);
        return match?.displayName || clientId;
    }, [clientsQuery.data, clientId]);

    const clientOptions = useMemo(
        () =>
            (clientsQuery.data ?? []).map((client) => ({
                value: client.clientId,
                label: client.displayName || client.clientId,
            })),
        [clientsQuery.data],
    );

    const matchedClientOption = clientOptions.find((option) => option.value === clientId);
    const clientNameResolved = Boolean(matchedClientOption) || !clientsQuery.isLoading;
    const selectedClientOption =
        clientId && clientNameResolved
            ? matchedClientOption ?? { value: clientId, label: selectedClientName }
            : null;

    const setClient = (nextClientId) => {
        setCursorStack([undefined]);
        setExportError("");
        const next = new URLSearchParams(searchParams);
        if (nextClientId) next.set("clientId", nextClientId);
        else next.delete("clientId");
        setSearchParams(next);
    };

    const exportCsv = async () => {
        setExporting(true);
        setExportError("");
        try {
            const rows = clientId
                ? await getClient(clientId).then((client) =>
                      !showInactiveClients && client?.status === "INACTIVE" ? [] : [client],
                  )
                : await listAllClients({ includeInactive: showInactiveClients });
            downloadCsv({
                columns: layout.columns,
                rows,
                filename: clientId ? `cdes-clients-${clientId}.csv` : "cdes-clients.csv",
            });
        } catch (error) {
            setExportError(error.message || "Export failed");
        } finally {
            setExporting(false);
        }
    };

    const page = cursorStack.length;
    const hasPages = page > 1 || Boolean(nextCursor);
    const clientCountLabel = `${clients.length} ${clients.length === 1 ? "client" : "clients"}`;

    const refreshClients = () =>
        Promise.all([
            queryClient.invalidateQueries({ queryKey: ["cdes-client-rows"] }),
            queryClient.invalidateQueries({ queryKey: ["cdes-clients"] }),
            queryClient.invalidateQueries({ queryKey: ["cdes-plans"] }),
        ]);

    const showInactiveClientRows = (checked) => {
        setCursorStack([undefined]);
        setExportError("");
        if (!checked && clientId) {
            const selected = (clientsQuery.data ?? []).find((client) => client.clientId === clientId);
            if (selected?.status === "INACTIVE") setClient("");
        }
    };

    const openCreate = () => {
        setFormError("");
        setEditingClient(null);
        setFormMode("create");
    };

    const openEdit = (client) => {
        setFormError("");
        setEditingClient(client);
        setFormMode("edit");
    };

    const closeForm = () => {
        if (saving) return;
        setFormMode(null);
        setEditingClient(null);
        setFormError("");
    };

    const submitForm = async (body) => {
        setSaving(true);
        setFormError("");
        try {
            if (formMode === "create") await createClient(body);
            else await updateClient(editingClient.clientId, body);
            await refreshClients();
            toast.success(formMode === "create" ? "Client created." : "Client updated.");
            setFormMode(null);
            setEditingClient(null);
        } catch (error) {
            setFormError(error.message || "Unable to save the client.");
        } finally {
            setSaving(false);
        }
    };

    const confirmDeactivate = async () => {
        try {
            await deactivateClient(deactivateTarget.clientId);
            await refreshClients();
            toast.success("Client deactivated.");
        } catch (error) {
            toast.error(error.message || "Unable to deactivate the client.");
            throw error;
        }
    };

    const confirmDelete = async () => {
        const targetId = deleteTarget.clientId;
        try {
            await deleteClient(targetId);
            if (clientId === targetId) setClient("");
            await refreshClients();
            toast.success("Client deleted.");
        } catch (error) {
            toast.error("Client deletion did not finish. Some records may already have been deleted.");
            await refreshClients();
            throw error;
        }
    };

    const reactivateClient = async (client) => {
        setReactivatingId(client.clientId);
        try {
            await updateClient(client.clientId, { status: "ACTIVE" });
            await refreshClients();
            toast.success("Client reactivated.");
        } catch (error) {
            toast.error(error.message || "Unable to reactivate the client.");
        } finally {
            setReactivatingId("");
        }
    };

    const renderActions = (client) => {
        const inactive = client.status === "INACTIVE";
        const name = client.displayName || client.clientId;
        const busy = reactivatingId === client.clientId || deleteTarget?.clientId === client.clientId;
        return (
            <td key="actions" {...cellProps(ACTIONS_COLUMN)}>
                <div className="cdes-client-actions">
                    <button
                        type="button"
                        className="outline-btn"
                        onClick={() => openEdit(client)}
                        disabled={busy}
                        aria-label={`Edit ${name}`}
                    >
                        Edit
                    </button>
                    {inactive ? (
                        <button
                            type="button"
                            className="outline-btn"
                            onClick={() => reactivateClient(client)}
                            disabled={busy}
                            aria-label={`Reactivate ${name}`}
                        >
                            {busy ? "Reactivating…" : "Reactivate"}
                        </button>
                    ) : (
                        <button
                            type="button"
                            className="outline-btn"
                            onClick={() => setDeactivateTarget(client)}
                            aria-label={`Deactivate ${name}`}
                        >
                            Deactivate
                        </button>
                    )}
                    <button
                        type="button"
                        className="outline-btn"
                        onClick={() => setDeleteTarget(client)}
                        disabled={busy}
                        aria-label={`Delete ${name}`}
                    >
                        Delete
                    </button>
                </div>
            </td>
        );
    };

    const renderCell = (client, column) => {
        if (column.key === "actions") return renderActions(client);
        if (column.key === "status") {
            return (
                <td key={column.key} {...cellProps(column)}>
                    <span className={`cdes-grid__status cdes-grid__status--${statusTone(client.status)}`}>
                        {statusLabel(client.status)}
                    </span>
                </td>
            );
        }
        if (column.key === "clientName" && client.clientId && !isBlank(client.displayName)) {
            return (
                <td key={column.key} {...cellProps(column)} title={client.displayName}>
                    <Link
                        className="cdes-grid__link"
                        to={`/finance/cdes/plans?clientId=${encodeURIComponent(client.clientId)}`}
                    >
                        {client.displayName}
                    </Link>
                </td>
            );
        }
        const value = column.value(client);
        if (isBlank(value)) {
            return (
                <td key={column.key} {...cellProps(column, "is-empty")}>
                    —
                </td>
            );
        }
        const text = column.key === "plans" ? formatPlanCount(value) : displayValue(value);
        return (
            <td key={column.key} {...cellProps(column)} title={text}>
                {text}
            </td>
        );
    };

    let body;
    if (pageQuery.isLoading) {
        body = (
            <div className="loader-wrapper">
                <div className="loader" />
            </div>
        );
    } else if (pageQuery.isError) {
        body = (
            <div className="cdes-grid__state">
                <p className="danger">{pageQuery.error.message || "Unable to load clients."}</p>
            </div>
        );
    } else if (clients.length === 0) {
        body = (
            <div className="cdes-grid__state">
                <p className="cdes-grid__state-title">No clients found</p>
                <p>
                    {showInactiveClients
                        ? "No clients match this view."
                        : "There are no active clients to display yet."}
                </p>
            </div>
        );
    } else {
        body = (
            <div className="table-wrapper cdes-grid__scroll">
                <table className="cdes-grid__table" style={{ width: layout.tableWidth, "--cdes-frozen-width": `${layout.frozenWidth}px` }}>
                    <colgroup>
                        {layout.tableColumns.map((column) => (
                            <col key={column.key} style={{ width: column.width }} />
                        ))}
                    </colgroup>
                    <thead>
                        <tr>
                            {layout.displayFrozen.map((column) => (
                                <th key={column.key} rowSpan={2} {...cellProps(column)} title={column.label}>
                                    {column.label}
                                </th>
                            ))}
                            {layout.categoryGroups.map((group) => (
                                <th
                                    key={group.category}
                                    colSpan={group.columns.length}
                                    className="cdes-grid__group is-group-start"
                                >
                                    <span className="cdes-grid__group-label">{group.category}</span>
                                </th>
                            ))}
                        </tr>
                        <tr>
                            {layout.categoryGroups.flatMap((group) =>
                                group.columns.map((column) => (
                                    <th
                                        key={column.key}
                                        {...cellProps(column, "cdes-grid__column")}
                                        title={column.label}
                                    >
                                        {column.label}
                                    </th>
                                )),
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {clients.map((client) => (
                            <tr key={client.clientId}>
                                {layout.tableColumns.map((column) => renderCell(client, column))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    return (
        <div className="cdes-grid">
            <div className="cdes-grid__header">
                <div>
                    <p className="title">Clients</p>
                    <p className="sub-title">
                        CDES clients with their configured client-level fields, one row per client.
                    </p>
                </div>
                <div className="cdes-grid__header-actions">
                    <button type="button" className="edit-btn cdes-grid__btn" onClick={openCreate}>
                        + Add Client
                    </button>
                    <button
                        type="button"
                        className="outline-btn cdes-grid__btn"
                        onClick={exportCsv}
                        disabled={exporting || pageQuery.isLoading}
                    >
                        {exporting ? <span className="loader loader--inline" /> : <FaDownload />}
                        {exporting ? "Exporting…" : "Export CSV"}
                    </button>
                </div>
            </div>

            <div className="cdes-grid__filter-stack">
            <div className="cdes-grid__filters">
                <div className="cdes-grid__field">
                    <label htmlFor="cdes-client-view-client">Client</label>
                    <Select
                        inputId="cdes-client-view-client"
                        className="react-select-container"
                        classNamePrefix="react-select"
                        placeholder={clientsQuery.isLoading ? "Loading clients…" : "All clients"}
                        options={clientOptions}
                        value={selectedClientOption}
                        onChange={(option) => setClient(option?.value || "")}
                        isLoading={clientsQuery.isLoading}
                        isDisabled={clientsQuery.isLoading}
                        isClearable
                        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                        styles={{
                            ...customStyles,
                            menuPortal: (base) => ({ ...base, zIndex: 200 }),
                        }}
                    />
                </div>
            </div>
                <div className="cdes-grid__checks">
                    <FormCheckbox
                        name="showInactiveClients"
                        label="Show inactive clients"
                        control={inactiveForm.control}
                        onSelectedHandler={showInactiveClientRows}
                    />
                </div>
            </div>

            {clientsQuery.isError && (
                <p className="danger">{clientsQuery.error.message || "Unable to load clients."}</p>
            )}
            {exportError && <p className="danger">{exportError}</p>}

            {(page > 1 || (pageQuery.isSuccess && clients.length > 0)) && (
                <div className="cdes-grid__summary">
                    <span className="loading-with-label">
                        {pageQuery.isSuccess &&
                            (!clientId
                                ? `${clientCountLabel} on this page`
                                : clientNameResolved
                                  ? `${clientCountLabel} for ${selectedClientName}`
                                  : clientCountLabel)}
                        {pageQuery.isFetching && <span className="loader loader--inline" />}
                    </span>
                    {hasPages && (
                        <div className="cdes-grid__pager" aria-label="Client pages">
                            <button
                                type="button"
                                className="outline-btn cdes-grid__btn"
                                onClick={() => setCursorStack((stack) => stack.slice(0, -1))}
                                disabled={page === 1 || pageQuery.isFetching}
                                aria-label="Previous page"
                            >
                                <FaChevronLeft />
                            </button>
                            <span>Page {page}</span>
                            <button
                                type="button"
                                className="outline-btn cdes-grid__btn"
                                onClick={() => setCursorStack((stack) => [...stack, nextCursor])}
                                disabled={!nextCursor || pageQuery.isFetching}
                                aria-label="Next page"
                            >
                                <FaChevronRight />
                            </button>
                        </div>
                    )}
                </div>
            )}

            {fieldsQuery.isError ? (
                <p className="danger" role="alert">
                    {fieldsQuery.error.message || "Unable to load field columns."}
                </p>
            ) : null}
            {body}
            {formMode ? (
                <ClientFormDialog
                    key={formMode === "edit" ? editingClient?.clientId : "create"}
                    mode={formMode}
                    client={editingClient}
                    saving={saving}
                    serverError={formError}
                    onClose={closeForm}
                    onSubmit={submitForm}
                />
            ) : null}
            <ConfirmationDialog
                open={Boolean(deactivateTarget)}
                onOpenChange={(open) => {
                    if (!open) setDeactivateTarget(null);
                }}
                title="Deactivate client"
                body={`${deactivateTarget?.displayName || deactivateTarget?.clientId || "This client"} will become inactive. The client's data will not be deleted, and its plans will not be deactivated.`}
                confirmLabel="Deactivate"
                confirmPendingLabel="Deactivating..."
                onConfirm={confirmDeactivate}
            />
            <ConfirmationDialog
                open={Boolean(deleteTarget)}
                onOpenChange={(open) => {
                    if (!open) setDeleteTarget(null);
                }}
                title="Delete client"
                body={`${deleteTarget?.displayName || deleteTarget?.clientId || "This client"} and its plans will be permanently deleted. This cannot be undone.`}
                confirmLabel="Delete"
                confirmPendingLabel="Deleting..."
                onConfirm={confirmDelete}
            />
        </div>
    );
};

export default ClientView;
